import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box, Chip, Divider, Stack, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { StyledIconButton } from '../../../components/Button'
import {
  BeakerOutlinedIcon,
  WrenchOutlinedIcon,
} from '../../../components/icons'
import { AppliancesSelector } from '../../../store/Appliances'
import { WaterTestKitRequestsSelectors } from '../../../store/waterTestKitRequests'
import { WaterTestKitResultsSelector } from '../../../store/waterTestKitResults'

type FiltrationOption = { value: string; label: string }

const formatTestDate = (
  uploadedAt: Date | string | undefined | null,
): string => {
  if (!uploadedAt) return 'N/A'
  return new Date(uploadedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const countParametersTested = (summary: any): string => {
  if (!summary) return '0 of 15'
  const total = Object.values(summary).reduce(
    (acc: number, val: any) => acc + (val?.count ?? 0),
    0,
  )
  return `${total} of 15`
}

const countIssues = (summary: any): number => {
  if (!summary) return 0
  return Object.entries(summary).reduce((acc, [k, v]: [string, any]) => {
    if (['Low', 'High'].includes(k)) acc += v?.count ?? 0
    return acc
  }, 0)
}

export const YourHomeProfileCard = () => {
  const theme = useTheme()
  const navigate = useNavigate()

  const waterTestRequest = useSelector(
    WaterTestKitRequestsSelectors.makeLatestResult,
  )
  const waterFiltration = useSelector(
    AppliancesSelector.waterFiltrationOptions,
  ) as FiltrationOption[]

  const latestTest = useSelector(WaterTestKitResultsSelector.makeLatestResult)
  const summary = useSelector(
    WaterTestKitResultsSelector.makeLatestResultsSummaryShort,
  )

  const systems = waterTestRequest?.systems ?? []

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: '12px',
        p: 3,
        width: '100%',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Your Home Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A snapshot of your equipment and most recent test, so you always know
        what you're working with.
      </Typography>

      <Stack spacing={2.5} divider={<Divider flexItem />}>
        {/* Current Equipment */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <StyledIconButton
              status="success"
              actionIcon={<WrenchOutlinedIcon />}
            />
            <Typography variant="h6" fontWeight={600}>
              Current equipment
            </Typography>
          </Stack>

          {systems.length === 0 ? (
            <Typography variant="body-small" color="text.secondary">
              No equipment on file yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {systems.map((code) => {
                const match = waterFiltration.find((f) => f.value === code)
                return (
                  <Stack
                    key={code}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body-small">
                      {match?.label ?? code}
                    </Typography>
                    <Chip label="active" size="small" variant="outlined-success" />
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Box>

        {/* Latest Test */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <StyledIconButton
              status="success"
              actionIcon={<BeakerOutlinedIcon />}
            />
            <Typography variant="h6" fontWeight={600}>
              Latest test
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            sx={{ mb: 2 }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body-small" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Test date
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatTestDate(latestTest?.uploadedAt)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body-small" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Parameters tested
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {countParametersTested(summary)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body-small" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Issues found
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {countIssues(summary)} Areas
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => navigate('/profile/results')}
          >
            <Typography variant="body-small" color="primary.main" fontWeight={500}>
              View full result
            </Typography>
            <ArrowForwardIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}
